// pages/Services.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getServices,
  getServiceCategories,
  getServicesByCategory
} from '../services/api';
import { useCart } from '../context/CartContext';
import {
  Search,
  Clock,
  Home,
  Settings,
  Wind,
  Droplets,
  Zap,
  Tv,
  Refrigerator,
  Microwave,
  Battery,
  Fan,
  Thermometer,
  Drill,
  Scissors,
  Bath,
  ChevronDown,
  ArrowLeft,
  Loader,
  ShoppingCart,
  Filter,
  X,
  Shield,
  Award,
  Star,
  TrendingUp,
  Sparkles
} from 'lucide-react';

const ServicesPage = ({ user, onLoginSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Services');
  const [categories, setCategories] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [priceFilter, setPriceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [showMobileCategories, setShowMobileCategories] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Logo image path
  const logoImage = '/logo.png';

  // Quick Add to Cart Modal
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddService, setQuickAddService] = useState(null);
  const [quickAddBHK, setQuickAddBHK] = useState('1BHK');
  const [quickAddVariant, setQuickAddVariant] = useState(null);
  const [quickAddQuantity, setQuickAddQuantity] = useState(1);

  // Primary color scheme
  const primaryColor = '#096651';
  const primaryLight = '#0d8a72';
  const accentColor = '#ff7e5f';
  const lightBg = '#f8fffd';
  const textDark = '#1a2b23';

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check for category selection from HomePage
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
    }
    if (location.state?.searchQuery) {
      setSearchQuery(location.state.searchQuery);
    }
  }, [location.state]);

  // Fetch services and categories
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');

        const [categoriesData, servicesData] = await Promise.all([
          getServiceCategories(),
          getServices()
        ]);

        // Filter out "All Services" if it exists in the API response
        const filteredCategories = categoriesData
          .map(cat => cat.name || cat)
          .filter(cat => cat !== 'All Services' && cat !== 'all' && cat !== 'All');

        // Add "All Services" only once at the beginning
        setCategories(['All Services', ...filteredCategories]);
        setAllServices(servicesData || []);
        setServicesList(servicesData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load services. Please try again.');
        setCategories(['All Services', 'Cleaning Services', 'AC Services', 'Handyman Services']);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter and sort services
  useEffect(() => {
    let filteredServices = [...allServices];

    // Apply category filter
    if (selectedCategory !== 'All Services') {
      filteredServices = filteredServices.filter(service =>
        service.category === selectedCategory
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredServices = filteredServices.filter(service =>
        service.name.toLowerCase().includes(query) ||
        (service.description && service.description.toLowerCase().includes(query)) ||
        (service.category && service.category.toLowerCase().includes(query))
      );
    }

    // Apply price filter
    if (priceFilter === 'under1000') {
      filteredServices = filteredServices.filter(service => {
        const price = getServicePriceValue(service);
        return price < 1000;
      });
    } else if (priceFilter === '1000-2000') {
      filteredServices = filteredServices.filter(service => {
        const price = getServicePriceValue(service);
        return price >= 1000 && price <= 2000;
      });
    } else if (priceFilter === 'over2000') {
      filteredServices = filteredServices.filter(service => {
        const price = getServicePriceValue(service);
        return price > 2000;
      });
    }

    // Apply sorting
    if (sortBy === 'price-low') {
      filteredServices.sort((a, b) => getServicePriceValue(a) - getServicePriceValue(b));
    } else if (sortBy === 'price-high') {
      filteredServices.sort((a, b) => getServicePriceValue(b) - getServicePriceValue(a));
    } else if (sortBy === 'rating') {
      filteredServices.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    } else if (sortBy === 'name') {
      filteredServices.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'popular') {
      // Sort popular services first (those with popular: true)
      filteredServices.sort((a, b) => {
        const aPopular = a.popular === true ? 1 : 0;
        const bPopular = b.popular === true ? 1 : 0;
        if (bPopular !== aPopular) {
          return bPopular - aPopular;
        }
        // If both are popular or both are not, sort by rating
        return (b.rating || 0) - (a.rating || 0);
      });
    }

    setServicesList(filteredServices);
  }, [selectedCategory, searchQuery, allServices, priceFilter, sortBy]);

  // Get popular services (services with popular: true)
  const popularServices = allServices.filter(service => service.popular === true);

  const getServicePriceValue = (service) => {
    if (service.pricingType === 'bhk-based' && service.bhkPrices) {
      return service.bhkPrices['1BHK'] || service.basePrice || 0;
    } else if (service.pricingType === 'variants' && service.variants && service.variants.length > 0) {
      return service.variants[0].price || service.basePrice || 0;
    } else if (service.pricingType === 'fixed') {
      return service.basePrice || 0;
    } else {
      return service.calculatedPrice || service.basePrice || 0;
    }
  };

  const handleServiceClick = (service) => {
    if (!user) {
      navigate('/', {
        state: {
          message: 'Please login to view service details',
          showAuth: true
        }
      });
      return;
    }

    // Navigate to service detail page instead of booking
    navigate(`/service/${service.id || service.name.replace(/\s+/g, '-').toLowerCase()}`, {
      state: { service }
    });
  };

  const handleQuickAddToCart = (service) => {
    if (!user) {
      navigate('/', {
        state: {
          message: 'Please login to add items to cart',
          showAuth: true
        }
      });
      return;
    }

    setQuickAddService(service);
    setQuickAddBHK('1BHK');
    setQuickAddQuantity(1);
    
    // Set default variant if available
    if (service.variants && service.variants.length > 0) {
      setQuickAddVariant(service.variants[0]);
    } else {
      setQuickAddVariant(null);
    }
    
    setShowQuickAddModal(true);
  };

  const confirmQuickAdd = () => {
    if (!quickAddService) return;

    const serviceToAdd = { ...quickAddService };
    
    if (quickAddService.pricingType === 'bhk-based') {
      serviceToAdd.selectedBHK = quickAddBHK;
      serviceToAdd.price = quickAddService.bhkPrices[quickAddBHK];
    } else if (quickAddService.pricingType === 'variants' && quickAddVariant) {
      serviceToAdd.selectedVariant = quickAddVariant;
      serviceToAdd.price = quickAddVariant.price;
      serviceToAdd.name = `${quickAddService.name} - ${quickAddVariant.name}`;
    } else {
      serviceToAdd.price = quickAddService.calculatedPrice || quickAddService.basePrice || quickAddService.price;
    }

    addToCart(serviceToAdd, quickAddQuantity);
    setShowQuickAddModal(false);
    setQuickAddService(null);
    
    // Show success message
    if (!isMobile) {
      alert(`${serviceToAdd.name} added to cart!`);
    }
  };

  const getServiceImages = (service) => {
    if (!service) return [];
    
    const imageData = service.images || service.image;
    
    if (!imageData) return [];
    
    if (typeof imageData === 'string') {
      try {
        return JSON.parse(imageData);
      } catch (e) {
        return [imageData];
      }
    }
    
    if (Array.isArray(imageData)) {
      return imageData;
    }
    
    return [];
  };

  const getServiceIcon = (serviceName) => {
    const iconProps = { size: 48 };
    
    const icons = {
      'House Cleaning': <Home {...iconProps} />,
      'Kitchen Cleaning': <Home {...iconProps} />,
      'Bathroom Cleaning': <Bath {...iconProps} />,
      'Sofa Cleaning': <Home {...iconProps} />,
      'Water Tank Cleaning': <Droplets {...iconProps} />,
      'AC Service': <Wind {...iconProps} />,
      'Split AC Jet Service': <Wind {...iconProps} />,
      'Split AC Dual Service': <Wind {...iconProps} />,
      'Split AC Triple Service': <Wind {...iconProps} />,
      'Split AC Quad Service': <Wind {...iconProps} />,
      'Window AC Jet Service': <Wind {...iconProps} />,
      'Window AC Dual Service': <Wind {...iconProps} />,
      'Window AC Triple Service': <Wind {...iconProps} />,
      'Window AC Quad Service': <Wind {...iconProps} />,
      'AC Repair': <Wind {...iconProps} />,
      'Split AC Installation': <Wind {...iconProps} />,
      'Window AC Installation': <Wind {...iconProps} />,
      'Split AC Install + Service Combo': <Wind {...iconProps} />,
      'Refrigerator Repair': <Refrigerator {...iconProps} />,
      'Washing Machine Repair': <Settings {...iconProps} />,
      'Washing Machine Top Load Service': <Settings {...iconProps} />,
      'Washing Machine Front Load Service': <Settings {...iconProps} />,
      'Washing Machine Semi Automatic Service': <Settings {...iconProps} />,
      'Washing Machine Installation': <Settings {...iconProps} />,
      'Microwave Oven Repair': <Microwave {...iconProps} />,
      'Hob/Stove Repair': <Settings {...iconProps} />,
      'Burner Hob Service/Cleaning': <Settings {...iconProps} />,
      '3 Burner Hob Service': <Settings {...iconProps} />,
      '4 Burner Hob Service': <Settings {...iconProps} />,
      '5 Burner Hob Service': <Settings {...iconProps} />,
      'Kitchen Chimney Repair': <Settings {...iconProps} />,
      'Kitchen Chimney Service': <Settings {...iconProps} />,
      'Kitchen Chimney Installation': <Settings {...iconProps} />,
      'Geyser Repair': <Thermometer {...iconProps} />,
      'Geyser Installation': <Thermometer {...iconProps} />,
      'Geyser Service': <Thermometer {...iconProps} />,
      'Geyser Service/Cleaning': <Thermometer {...iconProps} />,
      'RO/Water Purifier Repair': <Droplets {...iconProps} />,
      'RO/Water Purifier Installation': <Droplets {...iconProps} />,
      'RO/Water Purifier Service': <Droplets {...iconProps} />,
      'Air Cooler Repair': <Wind {...iconProps} />,
      'Fan Repair': <Fan {...iconProps} />,
      'Fan Installation': <Fan {...iconProps} />,
      '1 Fan Installation': <Fan {...iconProps} />,
      '2 Fan Installation': <Fan {...iconProps} />,
      '3 Fan Installation': <Fan {...iconProps} />,
      '4 Fan Installation': <Fan {...iconProps} />,
      'LED TV Repair': <Tv {...iconProps} />,
      'TV Installation': <Tv {...iconProps} />,
      '32 inch TV Installation': <Tv {...iconProps} />,
      '43 inch TV Installation': <Tv {...iconProps} />,
      '64 inch TV Installation': <Tv {...iconProps} />,
      '75 inch TV Installation': <Tv {...iconProps} />,
      'Inverter Repair': <Battery {...iconProps} />,
      'Electrician Service': <Zap {...iconProps} />,
      'Plumber Service': <Droplets {...iconProps} />,
      'Carpenter Service': <Drill {...iconProps} />,
      'Men Saloon': <Scissors {...iconProps} />,
      'Women Saloon': <Scissors {...iconProps} />
    };
    
    return icons[serviceName] || <Settings {...iconProps} />;
  };

  const getServicePriceDisplay = (service) => {
    if (service.pricingType === 'inspection') {
      return `₹${service.visitingCharge || service.basePrice} Visiting`;
    } else if (service.pricingType === 'bhk-based' && service.bhkPrices) {
      return `From ₹${service.bhkPrices['1BHK'] || service.basePrice || 0}`;
    } else if (service.pricingType === 'variants' && service.variants && service.variants.length > 0) {
      return `From ₹${service.variants[0].price || service.basePrice || 0}`;
    } else if (service.pricingType === 'fixed') {
      return `₹${service.basePrice || 0}`;
    } else {
      return `₹${service.calculatedPrice || service.basePrice || 0}`;
    }
  };

  // Styles
  const styles = {
    page: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      color: textDark,
      backgroundColor: '#ffffff',
      minHeight: '100vh',
    },
    header: {
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryLight} 100%)`,
      color: 'white',
      padding: isMobile ? '24px 0' : '40px 0',
      position: 'relative',
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 12px' : '0 20px',
    },
    headerContent: {
      maxWidth: '800px',
      margin: '0 auto',
      textAlign: 'center',
    },
    headerTitle: {
      fontSize: isMobile ? '1.5rem' : 'clamp(2rem, 4vw, 2.5rem)',
      fontWeight: '700',
      marginBottom: isMobile ? '8px' : '16px',
      lineHeight: '1.2',
    },
    headerSubtitle: {
      fontSize: isMobile ? '0.875rem' : 'clamp(1rem, 2vw, 1.1rem)',
      opacity: '0.9',
      marginBottom: isMobile ? '16px' : '30px',
      lineHeight: '1.4',
      padding: isMobile ? '0 8px' : '0',
    },
    // Loading State
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '50vh',
      gap: '20px',
      padding: '40px 20px',
    },
    loadingLogo: {
      width: isMobile ? '100px' : '150px',
      height: isMobile ? '100px' : '150px',
      objectFit: 'contain',
      animation: 'pulse 1.5s ease-in-out infinite',
    },
    loadingSpinner: {
      border: `4px solid ${lightBg}`,
      borderTop: `4px solid ${primaryColor}`,
      borderRadius: '50%',
      width: '40px',
      height: '40px',
      animation: 'spin 1s linear infinite',
    },
    // Popular Services Section
    popularSection: {
      padding: isMobile ? '30px 0' : '60px 0',
      backgroundColor: lightBg,
    },
    popularHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: isMobile ? '20px' : '30px',
    },
    popularTitleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    popularTitle: {
      fontSize: isMobile ? '1.25rem' : 'clamp(1.5rem, 3vw, 2rem)',
      fontWeight: '700',
      color: textDark,
    },
    popularSubtitle: {
      fontSize: isMobile ? '0.875rem' : '1rem',
      color: '#666',
      marginBottom: isMobile ? '20px' : '30px',
    },
    popularServicesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(280px, 1fr))' : 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: isMobile ? '15px' : '25px',
      marginBottom: isMobile ? '20px' : '40px',
    },
    // Search and Filter Section
    searchSection: {
      backgroundColor: 'white',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '16px' : '30px',
      marginTop: isMobile ? '-20px' : '-40px',
      position: 'relative',
      zIndex: 2,
      boxShadow: '0 10px 40px rgba(9, 102, 81, 0.15)',
      border: `1px solid ${primaryColor}10`,
    },
    searchBox: {
      display: 'flex',
      gap: '10px',
      marginBottom: '20px',
    },
    searchInput: {
      flex: 1,
      border: `2px solid ${primaryColor}20`,
      borderRadius: isMobile ? '10px' : '12px',
      padding: isMobile ? '12px 16px' : '16px 20px',
      fontSize: isMobile ? '14px' : '16px',
      outline: 'none',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    searchButton: {
      backgroundColor: primaryColor,
      color: 'white',
      border: 'none',
      borderRadius: isMobile ? '10px' : '12px',
      padding: isMobile ? '12px 20px' : '16px 30px',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '6px' : '10px',
      transition: 'all 0.3s ease',
      boxShadow: `0 4px 12px ${primaryColor}30`,
      whiteSpace: 'nowrap',
    },
    filterSection: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '20px',
      marginBottom: '30px',
      paddingBottom: '20px',
      borderBottom: `1px solid ${primaryColor}10`,
    },
    categoriesNav: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      alignItems: 'center',
      overflowX: 'auto',
      paddingBottom: '8px',
      scrollbarWidth: 'thin',
      scrollbarColor: `${primaryColor} ${lightBg}`,
      WebkitOverflowScrolling: 'touch',
    },
    categoryButton: {
      padding: isMobile ? '8px 16px' : '12px 24px',
      borderRadius: isMobile ? '8px' : '12px',
      border: `2px solid ${primaryColor}20`,
      backgroundColor: 'white',
      color: textDark,
      fontSize: isMobile ? '13px' : '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      whiteSpace: 'nowrap',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      position: 'relative',
      overflow: 'hidden',
    },
    activeCategoryButton: {
      backgroundColor: primaryColor,
      color: 'white',
      borderColor: primaryColor,
      boxShadow: `0 4px 12px ${primaryColor}40`,
      transform: 'translateY(-2px)',
    },
    filterControls: {
      display: 'flex',
      gap: '15px',
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    selectInput: {
      padding: '12px 20px',
      borderRadius: '10px',
      border: `2px solid ${primaryColor}20`,
      backgroundColor: 'white',
      color: textDark,
      fontSize: '15px',
      fontWeight: '500',
      outline: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    filterButton: {
      display: 'none',
      alignItems: 'center',
      gap: '8px',
      padding: '12px 20px',
      borderRadius: '10px',
      border: `2px solid ${primaryColor}20`,
      backgroundColor: 'white',
      color: textDark,
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    mobileCategoryToggle: {
      display: 'flex',
      width: '100%',
      padding: isMobile ? '10px 14px' : '14px 20px',
      borderRadius: isMobile ? '10px' : '12px',
      border: `2px solid ${primaryColor}20`,
      backgroundColor: 'white',
      color: textDark,
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      cursor: 'pointer',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: isMobile ? '12px' : '15px',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    },
    mobileFilters: {
      display: 'none',
      padding: '20px',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
      marginBottom: '20px',
    },
    filterGroup: {
      marginBottom: '20px',
    },
    filterLabel: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: textDark,
    },
    radioGroup: {
      display: 'flex',
      gap: '15px',
      flexWrap: 'wrap',
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '14px',
      cursor: 'pointer',
    },
    // Services Grid
    servicesGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: isMobile ? '12px' : '30px',
      marginBottom: isMobile ? '20px' : '40px',
      padding: '0',
    },
    serviceCard: {
      backgroundColor: 'white',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: '0 5px 20px rgba(0, 0, 0, 0.08)',
      border: `1px solid ${primaryColor}10`,
      transition: 'all 0.3s ease',
      cursor: 'pointer',
    },
    serviceImageWrapper: {
      position: 'relative',
      height: isMobile ? '140px' : '200px',
      overflow: 'hidden',
    },
    serviceImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease',
    },
    serviceIconWrapper: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: `${primaryColor}10`,
    },
    popularBadge: {
      position: 'absolute',
      top: '10px',
      left: '10px',
      backgroundColor: '#FFD700',
      color: '#000',
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '700',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      zIndex: 2,
    },
    imageCountBadge: {
      position: 'absolute',
      bottom: '10px',
      right: '10px',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      color: primaryColor,
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      backdropFilter: 'blur(4px)',
    },
    serviceContent: {
      padding: '20px',
    },
    serviceTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '10px',
      color: textDark,
      lineHeight: '1.4',
    },
    serviceDescription: {
      fontSize: '14px',
      color: '#666',
      marginBottom: '15px',
      lineHeight: '1.5',
      minHeight: '60px',
    },
    serviceMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '15px',
      paddingBottom: '15px',
      borderBottom: `1px solid ${primaryColor}10`,
    },
    serviceDuration: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      color: '#666',
    },
    serviceRating: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '13px',
      color: '#666',
    },
    servicePrice: {
      fontSize: isMobile ? '16px' : '20px',
      fontWeight: '700',
      color: primaryColor,
    },
    serviceActions: {
      display: 'flex',
      gap: '10px',
    },
    addToCartBtn: {
      flex: 1,
      backgroundColor: `${primaryColor}10`,
      color: primaryColor,
      border: `1px solid ${primaryColor}20`,
      padding: isMobile ? '8px' : '10px',
      borderRadius: isMobile ? '6px' : '8px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: isMobile ? '4px' : '6px',
      transition: 'all 0.3s ease',
    },
    bookNowBtn: {
      flex: 2,
      backgroundColor: primaryColor,
      color: 'white',
      border: 'none',
      padding: isMobile ? '8px' : '10px',
      borderRadius: isMobile ? '6px' : '8px',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    noServices: {
      textAlign: 'center',
      padding: '60px 20px',
      backgroundColor: lightBg,
      borderRadius: '16px',
    },
    resultsInfo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '20px',
      padding: '15px',
      backgroundColor: lightBg,
      borderRadius: '12px',
    },
    resultsCount: {
      fontSize: '16px',
      fontWeight: '600',
      color: textDark,
    },
    clearFilters: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: 'transparent',
      color: primaryColor,
      border: `1px solid ${primaryColor}`,
      padding: '8px 16px',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    // Modal Styles
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    quickAddModal: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '30px',
      maxWidth: '400px',
      width: '100%',
      position: 'relative',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    },
    modalClose: {
      position: 'absolute',
      top: '15px',
      right: '15px',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '24px',
      color: '#666',
      cursor: 'pointer',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
    },
    modalSection: {
      marginBottom: '20px',
    },
    bhkOptions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
      gap: '10px',
      marginTop: '10px',
    },
    bhkBtn: {
      padding: '10px',
      borderRadius: '8px',
      border: `1px solid ${primaryColor}20`,
      backgroundColor: 'white',
      color: textDark,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      transition: 'all 0.3s ease',
    },
    activeBhkBtn: {
      backgroundColor: primaryColor,
      color: 'white',
      borderColor: primaryColor,
    },
    variantOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      marginTop: '10px',
    },
    variantBtn: {
      padding: '12px 15px',
      borderRadius: '8px',
      border: `1px solid ${primaryColor}20`,
      backgroundColor: 'white',
      color: textDark,
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'all 0.3s ease',
    },
    activeVariantBtn: {
      backgroundColor: `${primaryColor}10`,
      borderColor: primaryColor,
      color: primaryColor,
    },
    quantityControls: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      marginTop: '10px',
    },
    quantityBtn: {
      width: '40px',
      height: '40px',
      borderRadius: '8px',
      border: `1px solid ${primaryColor}20`,
      backgroundColor: 'white',
      color: textDark,
      fontSize: '20px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.3s ease',
    },
    quantityDisplay: {
      fontSize: '18px',
      fontWeight: '600',
      color: textDark,
      minWidth: '40px',
      textAlign: 'center',
    },
    modalActions: {
      display: 'flex',
      gap: '15px',
      marginTop: '30px',
    },
    cancelBtn: {
      flex: 1,
      backgroundColor: 'transparent',
      color: textDark,
      border: `1px solid ${primaryColor}20`,
      padding: '12px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    addToCartModalBtn: {
      flex: 2,
      backgroundColor: primaryColor,
      color: 'white',
      border: 'none',
      padding: '12px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
    },
  };

  // Service Card Component
  const ServiceCard = ({ service, isPopular = false }) => {
    const images = getServiceImages(service);
    const primaryImage = images.length > 0 ? images[0] : null;
    
    return (
      <div
        style={styles.serviceCard}
        onClick={() => handleServiceClick(service)}
        onMouseEnter={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(-8px)';
            e.currentTarget.style.boxShadow = `0 20px 40px ${primaryColor}20`;
            e.currentTarget.style.borderColor = primaryColor;
            const serviceImage = e.currentTarget.querySelector('.service-image');
            if (serviceImage) {
              serviceImage.style.transform = 'scale(1.05)';
            }
          }
        }}
        onMouseLeave={(e) => {
          if (!isMobile) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 5px 20px rgba(0, 0, 0, 0.08)';
            e.currentTarget.style.borderColor = `${primaryColor}10`;
            const serviceImage = e.currentTarget.querySelector('.service-image');
            if (serviceImage) {
              serviceImage.style.transform = 'scale(1)';
            }
          }
        }}
      >
        <div style={styles.serviceImageWrapper}>
          {primaryImage ? (
            <>
              <img
                src={primaryImage}
                alt={service.name}
                className="service-image"
                style={styles.serviceImage}
                onError={(e) => {
                  e.target.style.display = 'none';
                  const nextSibling = e.target.nextElementSibling;
                  if (nextSibling) {
                    nextSibling.style.display = 'flex';
                  }
                }}
              />
              <div style={{ ...styles.serviceIconWrapper, display: 'none' }}>
                {getServiceIcon(service.name)}
              </div>
              {isPopular && service.popular && (
                <div style={styles.popularBadge}>
                  <TrendingUp size={12} />
                  Popular
                </div>
              )}
              {images.length > 1 && (
                <div style={styles.imageCountBadge}>
                  +{images.length - 1}
                </div>
              )}
            </>
          ) : (
            <div style={styles.serviceIconWrapper}>
              {getServiceIcon(service.name)}
              {isPopular && service.popular && (
                <div style={styles.popularBadge}>
                  <TrendingUp size={12} />
                  Popular
                </div>
              )}
            </div>
          )}
        </div>

        <div style={styles.serviceContent}>
          <h3 style={styles.serviceTitle}>{service.name}</h3>
          <p style={styles.serviceDescription}>
            {service.description && service.description.length > 100
              ? `${service.description.substring(0, 100)}...`
              : service.description || 'Professional service by verified experts'}
          </p>

          <div style={styles.serviceMeta}>
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <div style={styles.serviceDuration}>
                <Clock size={14} color={primaryColor} />
                <span>{service.duration || '1-2 hours'}</span>
              </div>
              {service.rating && (
                <div style={styles.serviceRating}>
                  <Star size={14} color="#FFD700" fill="#FFD700" />
                  <span>{service.rating.toFixed(1)}</span>
                </div>
              )}
            </div>
            <div style={styles.servicePrice}>
              {getServicePriceDisplay(service)}
            </div>
          </div>

          <div style={styles.serviceActions}>
            <button
              style={styles.addToCartBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleQuickAddToCart(service);
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = `${primaryColor}20`;
                  e.target.style.borderColor = primaryColor;
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = `${primaryColor}10`;
                  e.target.style.borderColor = `${primaryColor}20`;
                }
              }}
            >
              <ShoppingCart size={16} />
              Add to Cart
            </button>
            <button
              style={styles.bookNowBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleServiceClick(service);
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = primaryLight;
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = `0 6px 16px ${primaryColor}40`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = primaryColor;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 4px 12px ${primaryColor}30`;
                }
              }}
            >
              Book Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.container}>
            <div style={styles.headerContent}>
              <button
                style={{
                  position: isMobile ? 'relative' : 'absolute',
                  left: isMobile ? '0' : '20px',
                  top: isMobile ? '0' : '40px',
                  backgroundColor: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  border: isMobile ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                  padding: isMobile ? '8px 0' : '10px 20px',
                  borderRadius: '8px',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  marginBottom: isMobile ? '12px' : '0',
                  width: isMobile ? 'fit-content' : 'auto',
                  justifyContent: isMobile ? 'flex-start' : 'center',
                }}
                onClick={() => navigate('/')}
              >
                <ArrowLeft size={isMobile ? 14 : 16} />
                {isMobile ? 'Back' : 'Back to Home'}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
                <img 
                  src={logoImage} 
                  alt="EaseMyHome Logo" 
                  style={{ 
                    width: isMobile ? '50px' : '80px',
                    height: isMobile ? '50px' : '80px',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <Shield size={isMobile ? 32 : 40} color="white" style={{ display: 'none' }} />
              </div>
              
              <h1 style={styles.headerTitle}>Our Services</h1>
              <p style={styles.headerSubtitle}>
                Choose from our wide range of professional home services. Quality guaranteed with verified experts.
              </p>
            </div>
          </div>
        </div>

        {/* Loading State with Logo */}
        <div style={styles.loadingContainer}>
          <img 
            src={logoImage} 
            alt="Loading" 
            style={styles.loadingLogo}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'block';
            }}
          />
          <div style={styles.loadingSpinner}></div>
          <p style={{ color: primaryColor, fontWeight: '600' }}>Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.container}>
          <div style={styles.headerContent}>
            <button
              style={{
                position: isMobile ? 'relative' : 'absolute',
                left: isMobile ? '0' : '20px',
                top: isMobile ? '0' : '40px',
                backgroundColor: isMobile ? 'transparent' : 'rgba(255, 255, 255, 0.15)',
                color: 'white',
                border: isMobile ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                padding: isMobile ? '8px 0' : '10px 20px',
                borderRadius: '8px',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                marginBottom: isMobile ? '12px' : '0',
                width: isMobile ? 'fit-content' : 'auto',
                justifyContent: isMobile ? 'flex-start' : 'center',
              }}
              onClick={() => navigate('/')}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                }
              }}
            >
              <ArrowLeft size={isMobile ? 14 : 16} />
              {isMobile ? 'Back' : 'Back to Home'}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
              <img 
                src={logoImage} 
                alt="EaseMyHome Logo" 
                style={{ 
                  width: isMobile ? '50px' : '80px',
                  height: isMobile ? '50px' : '80px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <Shield size={isMobile ? 32 : 40} color="white" style={{ display: 'none' }} />
            </div>
            
            <h1 style={styles.headerTitle}>Our Services</h1>
            <p style={styles.headerSubtitle}>
              Choose from our wide range of professional home services. Quality guaranteed with verified experts.
            </p>
          </div>
        </div>
      </div>

      {/* Popular Services Section */}
      {popularServices.length > 0 && (
        <section style={styles.popularSection}>
          <div style={styles.container}>
            <div style={styles.popularHeader}>
              <div style={styles.popularTitleContainer}>
                <TrendingUp size={isMobile ? 24 : 28} color="#FFD700" />
                <h2 style={styles.popularTitle}>Most Popular Services</h2>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Star size={isMobile ? 16 : 18} color="#FFD700" fill="#FFD700" />
                <span style={{ fontWeight: '600', color: textDark, fontSize: isMobile ? '14px' : '16px' }}>
                  Customer Favorites
                </span>
              </div>
            </div>
            
            <p style={styles.popularSubtitle}>
              Services most loved by our customers. Highly rated and frequently booked.
            </p>
            
            <div style={styles.popularServicesGrid}>
              {popularServices.slice(0, isMobile ? 3 : 4).map((service, index) => (
                <ServiceCard key={`popular-${index}`} service={service} isPopular={true} />
              ))}
            </div>
            
            {popularServices.length > (isMobile ? 3 : 4) && (
              <div style={{ textAlign: 'center' }}>
                <button
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: primaryColor,
                    backgroundColor: 'transparent',
                    border: `2px solid ${primaryColor}`,
                    padding: isMobile ? '10px 20px' : '12px 30px',
                    borderRadius: '8px',
                    fontSize: isMobile ? '14px' : '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                  }}
                  onClick={() => setSortBy('popular')}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = `${primaryColor}05`;
                      e.target.style.transform = 'translateY(-2px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.transform = 'translateY(0)';
                    }
                  }}
                >
                  View All Popular Services
                  <ChevronDown size={isMobile ? 14 : 16} />
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Search and Filter Section */}
      <div style={styles.container}>
        <div style={styles.searchSection}>
          <div style={styles.searchBox} className="search-box">
            <input
              type="text"
              placeholder="Search for services... (e.g., AC repair, cleaning, electrician)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleServiceClick}
              style={styles.searchInput}
              onFocus={(e) => {
                e.target.style.borderColor = primaryColor;
                e.target.style.boxShadow = `0 0 0 3px ${primaryColor}15`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = `${primaryColor}20`;
                e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
              }}
            />
            <button
              style={styles.searchButton}
              onClick={handleServiceClick}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = primaryLight;
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = `0 6px 16px ${primaryColor}40`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.target.style.backgroundColor = primaryColor;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 4px 12px ${primaryColor}30`;
                }
              }}
            >
              <Search size={20} />
              Search
            </button>
          </div>

          {/* Categories Navigation */}
          <div style={styles.filterSection} className="filter-section">
            {/* Mobile Category Toggle */}
            {isMobile && (
              <button
                style={{
                  ...styles.mobileCategoryToggle,
                  ...(showMobileCategories ? { borderColor: primaryColor, backgroundColor: `${primaryColor}05` } : {})
                }}
                onClick={() => setShowMobileCategories(!showMobileCategories)}
              >
                <span>{selectedCategory}</span>
                <ChevronDown
                  size={20}
                  style={{
                    transform: showMobileCategories ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                  }}
                />
              </button>
            )}

            {/* Desktop & Mobile Categories */}
            <div
              style={{
                ...styles.categoriesNav,
                display: isMobile && !showMobileCategories ? 'none' : 'flex'
              }}
              className={isMobile && showMobileCategories ? 'mobile-categories' : ''}
            >
              {categories.map(category => (
                <button
                  key={category}
                  style={{
                    ...styles.categoryButton,
                    ...(selectedCategory === category ? styles.activeCategoryButton : {})
                  }}
                  onClick={() => {
                    setSelectedCategory(category);
                    if (isMobile) {
                      setShowMobileCategories(false);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (selectedCategory !== category) {
                      e.target.style.backgroundColor = `${primaryColor}10`;
                      e.target.style.borderColor = `${primaryColor}40`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedCategory !== category) {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.borderColor = `${primaryColor}20`;
                    }
                  }}
                >
                  {category}
                </button>
              ))}
            </div>

            <div style={styles.filterControls}>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={styles.selectInput}
                onFocus={(e) => {
                  e.target.style.borderColor = primaryColor;
                  e.target.style.boxShadow = `0 0 0 3px ${primaryColor}15`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = `${primaryColor}20`;
                  e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
                }}
              >
                <option value="popular">Sort by: Popular</option>
                <option value="name">Sort by: Name</option>
                <option value="price-low">Sort by: Price: Low to High</option>
                <option value="price-high">Sort by: Price: High to Low</option>
                <option value="rating">Sort by: Rating</option>
              </select>

              <button
                style={{
                  ...styles.filterButton,
                  display: isMobile ? 'flex' : 'none'
                }}
                onClick={() => setShowFilters(!showFilters)}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = primaryColor;
                  e.target.style.backgroundColor = `${primaryColor}05`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = `${primaryColor}20`;
                  e.target.style.backgroundColor = 'white';
                }}
              >
                <Filter size={16} />
                Filters
              </button>
            </div>
          </div>

          {/* Mobile Filters */}
          {showFilters && isMobile && (
            <div style={{
              ...styles.mobileFilters,
              display: 'block',
              animation: 'slideDown 0.3s ease-out'
            }}>
              <div style={styles.filterGroup}>
                <label style={styles.filterLabel}>Price Range:</label>
                <div style={styles.radioGroup}>
                  {[
                    { value: 'all', label: 'All Prices' },
                    { value: 'under1000', label: 'Under ₹1000' },
                    { value: '1000-2000', label: '₹1000 - ₹2000' },
                    { value: 'over2000', label: 'Over ₹2000' },
                  ].map(option => (
                    <label key={option.value} style={styles.radioLabel}>
                      <input
                        type="radio"
                        value={option.value}
                        checked={priceFilter === option.value}
                        onChange={(e) => setPriceFilter(e.target.value)}
                        style={{ marginRight: '6px' }}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>
              <button
                style={{
                  ...styles.clearFilters,
                  width: '100%',
                  justifyContent: 'center'
                }}
                onClick={() => {
                  setPriceFilter('all');
                  setShowFilters(false);
                }}
              >
                <X size={16} />
                Clear Price Filter
              </button>
            </div>
          )}

          {/* Results Info */}
          {servicesList.length > 0 && (
            <div style={styles.resultsInfo}>
              <div style={styles.resultsCount}>
                Showing {servicesList.length} {servicesList.length === 1 ? 'service' : 'services'}
                {selectedCategory !== 'All Services' && ` in ${selectedCategory}`}
                {searchQuery && ` for "${searchQuery}"`}
              </div>
              {(selectedCategory !== 'All Services' || searchQuery || priceFilter !== 'all') && (
                <button
                  style={styles.clearFilters}
                  onClick={() => {
                    setSelectedCategory('All Services');
                    setSearchQuery('');
                    setPriceFilter('all');
                    setSortBy('popular');
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = `${primaryColor}10`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <X size={16} />
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Services Grid */}
        <div style={{ padding: '40px 0' }}>
          {servicesList.length === 0 ? (
            <div style={styles.noServices}>
              <Search size={48} color={primaryColor} />
              <h3 style={{ margin: '20px 0 10px', color: textDark }}>No services found</h3>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                {searchQuery
                  ? `No results found for "${searchQuery}". Try different keywords or browse all services.`
                  : 'No services available in this category. Try another category.'}
              </p>
              <button
                style={{
                  ...styles.bookNowBtn,
                  padding: '12px 30px',
                }}
                onClick={() => {
                  setSelectedCategory('All Services');
                  setSearchQuery('');
                  setPriceFilter('all');
                  setSortBy('popular');
                }}
              >
                View All Services
              </button>
            </div>
          ) : (
            <div style={styles.servicesGrid}>
              {servicesList.map(service => (
                <ServiceCard key={service.id || service.name} service={service} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Quick Add to Cart Modal */}
      {showQuickAddModal && quickAddService && (
        <div style={styles.modalOverlay} onClick={() => setShowQuickAddModal(false)}>
          <div style={styles.quickAddModal} onClick={(e) => e.stopPropagation()}>
            <button style={styles.modalClose} onClick={() => setShowQuickAddModal(false)}>
              ×
            </button>

            <h3 style={{ marginBottom: '10px', color: textDark }}>Add to Cart</h3>
            <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
              {quickAddService.name}
            </p>

            {/* Fixed Price Display */}
            {quickAddService.pricingType === 'fixed' && (
              <div style={styles.modalSection}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '15px',
                  backgroundColor: `${primaryColor}05`,
                  borderRadius: '8px'
                }}>
                  <span style={{ fontWeight: '600', color: textDark }}>Price:</span>
                  <span style={{ fontSize: '20px', fontWeight: '700', color: primaryColor }}>
                    ₹{quickAddService.calculatedPrice || quickAddService.basePrice || quickAddService.price}
                  </span>
                </div>
              </div>
            )}

            {/* BHK Selection */}
            {quickAddService.pricingType === 'bhk-based' && quickAddService.bhkPrices && (
              <div style={styles.modalSection}>
                <label style={styles.filterLabel}>Select Apartment Size:</label>
                <div style={styles.bhkOptions}>
                  {Object.entries(quickAddService.bhkPrices).map(([bhk, price]) => (
                    <button
                      key={bhk}
                      style={{
                        ...styles.bhkBtn,
                        ...(quickAddBHK === bhk ? styles.activeBhkBtn : {})
                      }}
                      onClick={() => setQuickAddBHK(bhk)}
                    >
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>{bhk}</span>
                      <span style={{ fontSize: '12px', color: quickAddBHK === bhk ? 'white' : primaryColor, fontWeight: '600' }}>
                        ₹{price}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Variant Selection */}
            {quickAddService.pricingType === 'variants' && quickAddService.variants && (
              <div style={styles.modalSection}>
                <label style={styles.filterLabel}>Select Option:</label>
                <div style={styles.variantOptions}>
                  {quickAddService.variants.map((variant) => (
                    <button
                      key={variant.id}
                      style={{
                        ...styles.variantBtn,
                        ...(quickAddVariant?.id === variant.id ? styles.activeVariantBtn : {})
                      }}
                      onClick={() => setQuickAddVariant(variant)}
                    >
                      <span style={{ fontWeight: '500' }}>{variant.name}</span>
                      <span style={{ fontWeight: '600', color: quickAddVariant?.id === variant.id ? primaryColor : textDark }}>
                        ₹{variant.price}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity Selection */}
            <div style={styles.modalSection}>
              <label style={styles.filterLabel}>Quantity:</label>
              <div style={styles.quantityControls}>
                <button
                  style={styles.quantityBtn}
                  onClick={() => setQuickAddQuantity(Math.max(1, quickAddQuantity - 1))}
                  disabled={quickAddQuantity <= 1}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = `${primaryColor}10`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                >
                  -
                </button>
                <span style={styles.quantityDisplay}>{quickAddQuantity}</span>
                <button
                  style={styles.quantityBtn}
                  onClick={() => setQuickAddQuantity(quickAddQuantity + 1)}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = `${primaryColor}10`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                >
                  +
                </button>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                style={styles.cancelBtn}
                onClick={() => setShowQuickAddModal(false)}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = `${primaryColor}10`;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                Cancel
              </button>
              <button
                style={styles.addToCartModalBtn}
                onClick={confirmQuickAdd}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = primaryLight;
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = primaryColor;
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <ShoppingCart size={16} />
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS animations */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
              max-height: 0;
            }
            to {
              opacity: 1;
              transform: translateY(0);
              max-height: 400px;
            }
          }
          
          @keyframes pulse {
            0% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
            100% { transform: scale(1); opacity: 1; }
          }
          
          .service-card {
            animation: fadeIn 0.5s ease-out;
          }
          
          /* Responsive styles */
          @media (max-width: 1024px) {
            .services-grid { 
              grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); 
              gap: 24px;
            }
            .popular-services-grid {
              grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            }
          }
          
          @media (max-width: 768px) {
            .services-grid { 
              grid-template-columns: 1fr !important; 
              gap: 12px !important;
            }
            
            .popular-services-grid {
              grid-template-columns: 1fr !important;
              gap: 12px !important;
            }
            
            .categories-nav { 
              justify-content: flex-start;
              overflow-x: auto;
              -webkit-overflow-scrolling: touch;
              scrollbar-width: thin;
              padding-bottom: 12px;
              margin-top: 12px;
            }
            
            .categories-nav::-webkit-scrollbar {
              height: 6px;
            }
            
            .categories-nav::-webkit-scrollbar-track {
              background: #f0f0f0;
              border-radius: 10px;
            }
            
            .categories-nav::-webkit-scrollbar-thumb {
              background: ${primaryColor};
              border-radius: 10px;
            }
            
            .category-button {
              padding: 10px 20px;
              font-size: 14px;
              flex-shrink: 0;
            }
            
            .filter-controls { 
              width: 100%;
              margin-top: 15px;
            }
            
            .select-input {
              width: 100%;
            }
            
            .filter-button { 
              display: flex !important;
              width: 100%;
              justify-content: center;
            }
            
            .header-content button { 
              position: relative; 
              left: 0; 
              top: 0; 
              margin-bottom: 20px; 
            }
            
            .search-section {
              padding: 16px !important;
              margin-top: -20px !important;
            }
            
            .search-box {
              flex-direction: column !important;
              gap: 10px !important;
              margin-bottom: 16px !important;
            }
            
            .search-input {
              font-size: 14px !important;
              padding: 12px 16px !important;
              width: 100% !important;
            }
            
            .search-button {
              padding: 12px 20px !important;
              font-size: 14px !important;
              white-space: nowrap;
              width: 100% !important;
              justify-content: center !important;
            }
            
            .filter-section {
              flex-direction: column;
              gap: 15px;
            }
            
            .results-info {
              flex-direction: column;
              gap: 15px;
              align-items: flex-start;
            }
            
            .clear-filters {
              width: 100%;
              justify-content: center;
            }
            
            .service-card {
              border-radius: 12px;
            }
            
            .service-image-wrapper {
              height: 180px;
            }
            
            .service-content {
              padding: 16px;
            }
            
            .service-title {
              font-size: 16px;
            }
            
            .service-description {
              font-size: 13px;
              min-height: 50px;
            }
            
            .service-price {
              font-size: 18px;
            }
            
            .service-actions {
              flex-direction: column;
              gap: 8px;
            }
            
            .add-to-cart-btn,
            .book-now-btn {
              width: 100%;
              padding: 12px;
              font-size: 14px;
            }
            
            .popular-header {
              flex-direction: column;
              align-items: flex-start;
              gap: 12px;
            }
            
            .popular-title-container {
              gap: 8px;
            }
            
            .popular-title {
              font-size: 1.125rem;
            }
            
            .loading-logo {
              width: 80px !important;
              height: 80px !important;
            }
            
            .loading-spinner {
              width: 30px;
              height: 30px;
              border-width: 3px;
            }
          }
          
          @media (max-width: 480px) {
            .services-grid { 
              grid-template-columns: 1fr !important; 
              gap: 12px !important;
            }
            
            .search-box { 
              flex-direction: column !important; 
              gap: 10px !important;
            }
            
            .search-input {
              width: 100% !important;
              font-size: 14px !important;
              padding: 12px 14px !important;
            }
            
            .search-button { 
              width: 100% !important; 
              justify-content: center !important;
              font-size: 14px !important;
              padding: 12px 18px !important;
            }
            
            .category-button {
              padding: 8px 14px !important;
              font-size: 12px !important;
            }
            
            .service-image-wrapper {
              height: 130px !important;
            }
            
            .service-content {
              padding: 10px !important;
            }
            
            .service-title {
              font-size: 13px !important;
            }
            
            .service-description {
              font-size: 11px !important;
              min-height: 36px !important;
            }
            
            .service-price {
              font-size: 15px !important;
            }
            
            .add-to-cart-btn,
            .book-now-btn {
              font-size: 12px !important;
              padding: 8px !important;
              min-height: 38px !important;
            }
            
            .modal-actions { 
              flex-direction: column !important; 
              gap: 10px !important;
            }
            
            .cancel-btn, 
            .add-to-cart-modal-btn { 
              width: 100% !important; 
            }
            
            .quick-add-modal {
              padding: 16px !important;
              max-width: 95% !important;
            }
            
            .bhk-options {
              grid-template-columns: repeat(2, 1fr) !important;
              gap: 8px !important;
            }
            
            .header-title {
              font-size: 1.375rem !important;
            }
            
            .header-subtitle {
              font-size: 0.8125rem !important;
            }
            
            .search-section {
              padding: 14px !important;
              margin-top: -18px !important;
            }
            
            .header {
              padding: 20px 0 !important;
            }
            
            .container {
              padding: 0 10px !important;
            }
            
            .popular-section {
              padding: 20px 0 !important;
            }
            
            .popular-title {
              font-size: 1rem !important;
            }
            
            .popular-subtitle {
              font-size: 0.75rem !important;
            }
            
            .loading-container {
              min-height: 40vh;
              padding: 20px;
            }
            
            .loading-logo {
              width: 60px !important;
              height: 60px !important;
            }
          }
          
          @media (max-width: 360px) {
            .services-grid {
              gap: 10px !important;
            }
            
            .service-card {
              border-radius: 8px !important;
            }
            
            .service-image-wrapper {
              height: 120px !important;
            }
            
            .service-content {
              padding: 10px !important;
            }
            
            .service-title {
              font-size: 12px !important;
            }
            
            .service-description {
              font-size: 11px !important;
            }
            
            .service-price {
              font-size: 14px !important;
            }
            
            .add-to-cart-btn,
            .book-now-btn {
              font-size: 11px !important;
              padding: 8px !important;
            }
            
            .search-section {
              padding: 12px !important;
            }
            
            .header-title {
              font-size: 1.25rem !important;
            }
            
            .popular-title {
              font-size: 0.875rem !important;
            }
            
            .loading-logo {
              width: 50px !important;
              height: 50px !important;
            }
          }
          
          /* Hide filter button on desktop, show on mobile */
          @media (min-width: 769px) {
            .mobile-filter-btn {
              display: none !important;
            }
          }
          
          @media (max-width: 768px) {
            .desktop-filter-controls {
              display: none !important;
            }
            
            .mobile-filter-btn {
              display: flex !important;
              align-items: center;
              gap: 8px;
              padding: 10px 20px;
              border-radius: 8px;
              border: 1px solid ${primaryColor}20;
              background-color: white;
              color: ${textDark};
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
            }
          }
          
          /* Category button hover effects */
          .category-button::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
            transition: left 0.5s ease;
          }
          
          .category-button:hover::before {
            left: 100%;
          }
          
          /* Smooth scroll for categories */
          .categories-nav {
            scroll-behavior: smooth;
          }
          
          /* Active category indicator */
          .category-button.active::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            transform: translateX(-50%);
            width: 30px;
            height: 3px;
            background: white;
            border-radius: 2px;
          }
          
          /* Mobile category dropdown animation */
          .mobile-categories {
            animation: slideDown 0.3s ease-out;
            max-height: 400px;
            overflow-y: auto;
            border: 1px solid ${primaryColor}20;
            border-radius: 12px;
            padding: 12px;
            background: white;
            margin-top: 12px;
          }
          
          /* Desktop: Hide mobile toggle, show categories */
          @media (min-width: 769px) {
            .mobile-category-toggle {
              display: none !important;
            }
          }
          
          /* Mobile: Better category layout */
          @media (max-width: 768px) {
            .categories-nav.mobile-categories {
              flex-direction: column;
              gap: 8px;
              max-height: 60vh;
              overflow-y: auto;
            }
            
            .categories-nav.mobile-categories .category-button {
              width: 100%;
              text-align: left;
              justify-content: flex-start;
              border-radius: 10px;
            }
            
            .filter-section {
              position: relative;
            }
          }
          
          /* Improve category button active state */
          .category-button.active {
            position: relative;
          }
          
          .category-button.active::after {
            content: '';
            position: absolute;
            bottom: 4px;
            left: 50%;
            transform: translateX(-50%);
            width: 40%;
            height: 2px;
            background: rgba(255, 255, 255, 0.8);
            border-radius: 2px;
          }
          
          /* Better hover effects */
          .category-button:not(.active):hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px ${primaryColor}25;
          }
          
          /* Smooth transitions */
          .category-button {
            will-change: transform;
          }
          
          /* Improve scrollbar for category nav */
          .categories-nav::-webkit-scrollbar {
            height: 6px;
          }
          
          .categories-nav::-webkit-scrollbar-track {
            background: ${lightBg};
            border-radius: 10px;
          }
          
          .categories-nav::-webkit-scrollbar-thumb {
            background: ${primaryColor};
            border-radius: 10px;
          }
          
          .categories-nav::-webkit-scrollbar-thumb:hover {
            background: ${primaryLight};
          }
          
          /* Focus states for accessibility */
          .category-button:focus-visible,
          .search-input:focus-visible,
          .select-input:focus-visible,
          .filter-button:focus-visible {
            outline: 2px solid ${primaryColor};
            outline-offset: 2px;
          }
          
          /* Improve service card on mobile */
          @media (max-width: 768px) {
            .service-card {
              border-radius: 10px !important;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
            }
            
            .service-card:active {
              transform: scale(0.98);
              box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1) !important;
            }
            
            /* Compact service meta */
            .service-meta {
              flex-wrap: wrap;
              gap: 8px;
            }
            
            .service-duration {
              font-size: 11px !important;
            }
            
            .service-duration svg {
              width: 12px !important;
              height: 12px !important;
            }
          }
          
          /* Better touch targets on mobile */
          @media (max-width: 768px) {
            .category-button,
            .add-to-cart-btn,
            .book-now-btn {
              min-height: 44px;
              touch-action: manipulation;
            }
            
            /* Ensure proper viewport scaling */
            * {
              -webkit-tap-highlight-color: transparent;
            }
            
            /* Prevent text size adjustment */
            html {
              -webkit-text-size-adjust: 100%;
              text-size-adjust: 100%;
            }
            
            /* Better spacing */
            .filter-section {
              gap: 12px !important;
            }
            
            .results-info {
              padding: 12px !important;
              font-size: 13px !important;
            }
            
            .results-count {
              font-size: 13px !important;
            }
            
            .clear-filters {
              padding: 8px 14px !important;
              font-size: 13px !important;
            }
          }
          
          /* Loading state improvements */
          .loading-spinner {
            border-width: 4px;
          }
          
          /* No services state */
          .no-services {
            padding: 60px 20px;
          }
          
          @media (max-width: 768px) {
            .no-services {
              padding: 40px 16px;
            }
          }
          
          /* Results info responsive */
          @media (max-width: 480px) {
            .results-info {
              padding: 12px;
            }
            
            .results-count {
              font-size: 14px;
            }
            
            .clear-filters {
              font-size: 13px;
              padding: 8px 14px;
            }
          }
          
          /* Modal improvements */
          .bhk-btn.active, .variant-btn.active {
            border-color: ${primaryColor} !important;
            background-color: ${primaryColor}10 !important;
          }
          
          .bhk-btn:active, .variant-btn:active {
            transform: scale(0.95);
            transition: transform 0.1s ease;
          }
          
          .quantity-btn:active {
            background-color: ${primaryColor}20 !important;
          }
          
          .add-to-cart-modal-btn:active {
            background-color: ${primaryLight} !important;
            transform: translateY(2px) !important;
          }
          
          /* Popular badge animation */
          .popular-badge {
            animation: pulse 2s infinite;
          }
        `}
      </style>
    </div>
  );
};

export default ServicesPage;